// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { Clickable, Icon, Link, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyComponentProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel, { SwiperStyle } from "@rc/App/Components/XngCarousel/XngCarousel";
import { createLinkIconStyles } from "@rc/App/SharedStyles/sharedStyles";
import ArrowRight from "@rc/XDL/Icons/ArrowRight";
import { color } from "@rc/XDL/Utilities";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

interface OwnProps {}

type Props = OwnProps;

export interface QuickOrderOverlayStyles {
    wrapper: InjectableCss;
    arrow: InjectableCss;
    close: ButtonPresentationProps;
    content: InjectableCss;
    title: TypographyProps;
    swiperStyles?: SwiperStyle;
    message: TypographyProps;
    actions: InjectableCss;
    next: LinkPresentationProps;
}

export const QuickOrderOverlayStyles: QuickOrderOverlayStyles = {
    wrapper: {
        css: css`
            position: absolute;
            box-sizing: border-box;
            width: 340px;
            top: 33px;
            left: -170%;
            border: 1px solid ${color("common.ui3")};
            border-radius: 3px;
            background-color: ${color("common.ui8")};
        `,
    },
    arrow: {
        css: css`
            position: absolute;
            top: -10px;
            left: 50%;
            transform: translateX(-50%);
            border-bottom: 10px solid ${color("common.ui3")};
            border-left: 13px solid transparent;
            border-right: 13px solid transparent;
            &::after {
                content: "";
                position: absolute;
                left: -12px;
                bottom: -10px;
                right: -12px;
                border-bottom: 9px solid ${color("common.ui8")};
                border-left: 12px solid transparent;
                border-right: 12px solid transparent;
            }
        `,
    },
    close: {
        css: css`
            position: absolute;
            top: 20px;
            right: 10px;
            cursor: pointer;
            z-index: 9;
            &:hover svg {
                color: ${color("primary")};
            }
        `,
    },
    content: {
        css: css`
            padding-left: 30px;
        `,
    },
    title: {
        as: "h3",
        variant: "h3",
        css: css`
            font-weight: 300;
            font-size: 20px;
            letter-spacing: 0;
            line-height: 24px;
            margin-top: 22px;
            margin-bottom: 10px;
            color: ${color("primary")};
        `,
    },
    swiperStyles: {
        container: {
            css: css`
                padding: 0 0 45px 0 !important;
                background: transparent;
                .prev-outside,
                .next-outside {
                    display: none;
                }
                .swiper-container {
                    position: static;
                }
                .swiper-pagination.swiper-pagination-bullets {
                    position: absolute;
                    bottom: 23px;
                    justify-content: flex-start;
                }
                .swiper-pagination-bullet {
                    width: 8px !important;
                    height: 8px !important;
                }
            `,
        },
    },
    message: {
        css: css`
            font-size: 14px;
            letter-spacing: 0;
            line-height: 24px;
            margin: 0;
            color: ${color("text.main")};
        `,
    },
    actions: {
        css: css`
            position: absolute;
            right: 15px;
            bottom: 15px;
            cursor: pointer;
        `,
    },
    next: {
        ...createLinkIconStyles(ArrowRight, { color: "primary" }),
        css: css`
            display: flex;
            flex-direction: row-reverse;
            ${IconWrapper} {
                width: 30px;
                height: 20px;
                margin-right: -8px;
                svg {
                    margin-right: 0;
                    margin-left: 8px;
                    height: 20px;
                    width: 20px;
                }
            }
        `,
    },
};

export const quickOrderOverlayStyles = QuickOrderOverlayStyles;

const styles = quickOrderOverlayStyles;

const QuickOrderOverlay: React.FC<Props> = () => {
    const [step, setStep] = useState(1);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const localStorageValue = window?.localStorage?.getItem("hideQuickOrderOverlay");
        setIsVisible(localStorageValue !== "true");
    }, []);

    const onClose = () => {
        window?.localStorage?.setItem("hideQuickOrderOverlay", "true");
        setIsVisible(false);
    };

    const goNext = () => {
        (document.querySelectorAll(".quick-order-item-wrapper .next-outside")[0] as HTMLElement).click();
        setStep(2);
    };

    return (
        <>
            {isVisible && (
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.arrow}></StyledWrapper>
                    <Clickable
                        {...styles.close}
                        onClick={onClose}
                        data-test-selector="quickOrder_close"
                        aria-label="close"
                    >
                        <Icon src="Close" size={14} color="common.ui5" />
                    </Clickable>
                    <StyledWrapper {...styles.content}>
                        <Typography {...styles.title}>{translate("Quick Order")}</Typography>
                        <XngCarousel
                            navigationOutSide={true}
                            slidesPerView={1}
                            spaceBetween={0}
                            extendedStyles={styles.swiperStyles}
                            navigation={true}
                            pagination={true}
                        >
                            <StyledWrapper>
                                <Typography as="p" {...styles.message}>
                                    {translate(
                                        "Already know what you’re looking for? Quickly shop for products using our easy-to-use Quick Order feature.",
                                    )}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="p" {...styles.message}>
                                    {translate(
                                        "Simply enter a product number or description to start your search. You can add multiple products to your cart, all from one page.",
                                    )}
                                </Typography>
                            </StyledWrapper>
                        </XngCarousel>

                        <StyledWrapper {...styles.actions}>
                            {step === 1 ? (
                                <Link
                                    {...styles.next}
                                    onClick={goNext}
                                    data-test-selector="quickOrder_close"
                                    aria-label="close"
                                >
                                    {translate("Next")}
                                </Link>
                            ) : (
                                <Link
                                    {...styles.next}
                                    onClick={onClose}
                                    data-test-selector="quickOrder_close"
                                    aria-label="close"
                                >
                                    {translate("Done")}
                                </Link>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export default QuickOrderOverlay;
